import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import useTokenStore from "../store/store";
import useThemeStore from "../store/themeStore";
import { ThemeProps } from "../utils/theme";
import backgroundPattern from "../images/vesta/vesta-property-pattern-01-teal-bg.svg";
import { useFavoritesStore } from "../store/favoritesStore";
import FavoritesModal from "./components/FavoritesModal";
import { 
  BarChart3, 
  Heart, 
  Home, 
  Calendar, 
  ExternalLink, 
  MapPin, 
  Edit3, 
  Zap, 
  Sparkles,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  User
} from "lucide-react";
import { useUserEnquiries } from "../hooks/useUserEnquiries";


interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  themeColor?: string;
  textColor?: string;
}

const ImprovedPagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  themeColor = "#3b82f6",
  textColor = "#ffffff"
}) => {
  // Calculate visible page range
  const getVisiblePages = () => {
    const delta = 1; // Pages to show on each side of current page
    const rangeWithDots = [];

    if (totalPages <= 7) {
      // Show all pages if total is 7 or less
      for (let i = 1; i <= totalPages; i++) {
        rangeWithDots.push(i);
      }
    } else {
      // Always show first page
      rangeWithDots.push(1);

      // Calculate start and end of middle range
      const start = Math.max(2, currentPage - delta);
      const end = Math.min(totalPages - 1, currentPage + delta);

      // Add dots after first page if needed
      if (start > 2) {
        rangeWithDots.push('dots1');
      }

      // Add middle range
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          rangeWithDots.push(i);
        }
      }

      // Add dots before last page if needed
      if (end < totalPages - 1) {
        rangeWithDots.push('dots2');
      }

      // Always show last page if more than 1 page
      if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }
    }

    return rangeWithDots;
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 pt-6">
      {/* Results info */}
      <div className="text-sm text-gray-700">
        Showing <span className="font-medium">{startItem}</span> to{' '}
        <span className="font-medium">{endItem}</span> of{' '}
        <span className="font-medium">{totalItems}</span> results
      </div>

      {/* Pagination controls */}
      <div className="flex items-center space-x-1">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500 transition-colors duration-200"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        {/* Page numbers */}
        <div className="hidden sm:flex items-center space-x-1 mx-2">
          {getVisiblePages().map((page, index) => {
            if (typeof page === 'string') {
              return (
                <span
                  key={`dots-${index}`}
                  className="px-3 py-2 text-sm font-medium text-gray-400"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </span>
              );
            }

            const isActive = currentPage === page;
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive
                    ? 'shadow-sm'
                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                }`}
                style={
                  isActive
                    ? {
                        backgroundColor: themeColor,
                        color: textColor,
                      }
                    : {}
                }
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Mobile page indicator */}
        <div className="sm:hidden px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">
          {currentPage} / {totalPages}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500 transition-colors duration-200"
        >
          Next
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const { favorites, isLoading: favoritesLoading, fetchFavorites } = useFavoritesStore();
  const { enquiries: servicesApplied, loading: enquiriesLoading, error: enquiriesError, refetch: refetchEnquiries } = useUserEnquiries();
  const [isFavoritesModalOpen, setIsFavoritesModalOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  // Redirect if not logged in
  useEffect(() => {
    if (!getToken) {
      navigate("/login");
    }
  }, [getToken, navigate]);

  // Fetch favorites when component mounts
  useEffect(() => {
    if (getToken) {
      fetchFavorites();
    }
  }, [getToken, fetchFavorites]);

  // Sort services by newest first (date descending)
  const sortedServices = [...servicesApplied].sort((a, b) => 
    new Date(b.appliedDate).getTime() - new Date(a.appliedDate).getTime()
  );

  // Calculate pagination
  const totalPages = Math.ceil(sortedServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = sortedServices.slice(startIndex, endIndex);

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [servicesApplied.length]);

  // White color detection function
  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    
    return false;
  };

  // Get contrasting text color for better visibility
  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!getToken) {
    return null; // Will redirect to login
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Dashboard | {getThemes?.name || 'Property Investment Platform'}</title>
        <meta name="description" content="Your property investment dashboard with favorites, purchases, and services" />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-2" style={{ color: getThemes?.txtColor }}>
              Dashboard
            </h1>
            <p className="text-lg" style={{ color: getThemes?.txtColor }}>
              Manage your property investments and services
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 ease-in-out">
              <div className="flex items-center">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                  style={{ backgroundColor: getThemes?.color1 }}
                >
                  <Heart className="w-6 h-6" style={{ color: getThemes?.txtColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Favorites</p>
                  <p className="text-2xl font-bold text-gray-900">{favorites.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 ease-in-out">
              <div className="flex items-center">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                  style={{ backgroundColor: getThemes?.color2 }}
                >
                  <Home className="w-6 h-6" style={{ color: getThemes?.txtColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Properties Purchased</p>
                  <p className="text-2xl font-bold text-gray-900">0</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl hover:scale-[1.03] transition-all duration-300 ease-in-out">
              <div className="flex items-center">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                  style={{ backgroundColor: getThemes?.color1 }}
                >
                  <BarChart3 className="w-6 h-6" style={{ color: getThemes?.txtColor }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Services Applied</p>
                  <p className="text-2xl font-bold text-gray-900">{servicesApplied.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Favorites Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ease-in-out">
              <div
                className="px-6 py-4"
                style={{ background: getThemes?.color1 }}
              >
                <div className="flex items-center">
                  <Heart className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
                  <h2 
                    className="text-xl font-bold"
                    style={{ color: getContrastColor(getThemes?.color1 || "") }}
                  >
                    Favorite Properties
                  </h2>
                </div>
              </div>
              
              <div className="p-6">
                {favoritesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: getThemes?.color1 }}></div>
                    <span className="ml-3 text-gray-600">Loading favorites...</span>
                  </div>
                ) : favorites.length === 0 ? (
                  <div className="text-center py-8">
                    <Heart className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 mb-4">No favorite properties yet</p>
                    <button
                      onClick={() => navigate("/listing")}
                      className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                      style={{
                        backgroundColor: getThemes?.color2 || "#0055cc",
                        color: getContrastColor(getThemes?.color2 || "#0055cc"),
                      }}
                    >
                      Browse Properties
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Scrollable container for favorites */}
                    <div className="max-h-96 overflow-y-auto pr-2 space-y-4">
                      {favorites.map((property) => (
                        <div key={property._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-2">
                                <MapPin className="w-4 h-4 text-gray-500 mr-2" />
                                <h3 className="font-semibold text-gray-900 text-sm">{property.fullAddress}</h3>
                              </div>
                              <div className="flex items-center space-x-4 text-sm text-gray-600">
                                <span>{property.bedrooms} bed</span>
                                <span>{property.region}</span>
                                <span className="font-semibold text-green-600">{formatPrice(property.price)}</span>
                              </div>
                              <div className="mt-2 text-xs text-gray-500">
                                Recently added
                              </div>
                            </div>
                            <button
                              onClick={() => navigate(`/listing/${property._id}`)}
                              className="ml-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                            >
                              <ExternalLink className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Edit Favorites Button */}
                    <div className="text-center pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setIsFavoritesModalOpen(true)}
                        className="inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-md text-sm"
                        style={{
                          backgroundColor: getThemes?.color2 || "#0055cc",
                          color: getContrastColor(getThemes?.color2 || "#0055cc"),
                        }}
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit Favorites
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Properties Purchased Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ease-in-out">
              <div
                className="px-6 py-4"
                style={{ background: getThemes?.color2 }}
              >
                <div className="flex items-center">
                  <Home className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color2 || "") }} />
                  <h2 
                    className="text-xl font-bold"
                    style={{ color: getContrastColor(getThemes?.color2 || "") }}
                  >
                    Properties Purchased
                  </h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="text-center py-8">
                  <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg inline-block mb-4">
                    <span className="font-semibold">Coming Soon</span>
                  </div>
                  <Sparkles className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Track your property purchases and investment history</p>
                  <p className="text-sm text-gray-500">This feature will show your completed property transactions with details like purchase date, address, and property link.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Services Applied For Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 ease-in-out">
            <div
              className="px-6 py-4"
              style={{ background: getThemes?.color1 }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <BarChart3 className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
                  <h2 
                    className="text-xl font-bold"
                    style={{ color: getContrastColor(getThemes?.color1 || "") }}
                  >
                    Services Applied For
                  </h2>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {enquiriesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: getThemes?.color1 }}></div>
                  <span className="ml-3 text-gray-600">Loading services...</span>
                </div>
              ) : enquiriesError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 mb-4">Error loading services: {enquiriesError}</p>
                  <button
                    onClick={refetchEnquiries}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Try Again
                  </button>
                </div>
              ) : servicesApplied.length === 0 ? (
                <div className="text-center py-8">
                  <BarChart3 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">No services applied for yet</p>
                  <button
                    onClick={() => navigate("/services")}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-md"
                    style={{
                      backgroundColor: getThemes?.color2 || "#0055cc",
                      color: getContrastColor(getThemes?.color2 || "#0055cc"),
                    }}
                  >
                    View Services
                  </button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Service Name</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Applied Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentServices.map((service) => (
                          <tr key={service.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-left">
                              <div className="font-medium text-gray-900">{service.serviceName}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center text-gray-600">
                                <Calendar className="w-4 h-4 mr-2" />
                                {formatDate(service.appliedDate)}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-left">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(service.status)}`}>
                                {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Improved Pagination */}
                  <ImprovedPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={servicesApplied.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    themeColor={getThemes?.color1}
                    textColor={getContrastColor(getThemes?.color1 || "")}
                  />
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 ease-in-out">
            <div className="flex items-center justify-center mb-4">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                style={{ backgroundColor: getThemes?.color1 }}
              >
                <Zap className="w-5 h-5" style={{ color: getThemes?.txtColor }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Quick Actions
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => navigate("/listing")}
                className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
              >
                <Home className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
                <div>
                  <div className="font-medium text-gray-900">Browse Properties</div>
                  <div className="text-sm text-gray-500">Find your next investment</div>
                </div>
              </button>
              
              <button
                onClick={() => navigate("/services")}
                className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
              >
                <BarChart3 className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
                <div>
                  <div className="font-medium text-gray-900">View Services</div>
                  <div className="text-sm text-gray-500">Explore our offerings</div>
                </div>
              </button>
              
              <button
                onClick={() => navigate("/profile")}
                className="flex items-center p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200 text-left group"
              >
                <User className="w-6 h-6 mr-3 group-hover:scale-110 transition-transform duration-200" style={{ color: "black" }} />
                <div>
                  <div className="font-medium text-gray-900">Manage Profile</div>
                  <div className="text-sm text-gray-500">Update your information</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Favorites Modal */}
      <FavoritesModal
        isOpen={isFavoritesModalOpen}
        onClose={() => setIsFavoritesModalOpen(false)}
        themeColor={getThemes?.color1 || "#000000"}
        textColor={getThemes?.txtColor || "#ffffff"}
      />
    </div>
  );
}

export default Dashboard;